'use client';

import { useSession } from '@/lib/auth/client';
import { useRouter, usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { SubscriptionService } from '@/services/api/subscription.service';
import type { SubscriptionSummaryDto } from '@/types/api/auth.responses';

type RecoveredSubscription = {
  tenantId: string;
  subscription: SubscriptionSummaryDto;
};

export default function SubscriptionGuard({ children }: { children: React.ReactNode }) {
  const { data: session, status, update } = useSession();
  const router = useRouter();
  const pathname = usePathname();
  const toastShownRef = useRef(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const attemptedTenantIdRef = useRef<string | null>(null);
  const activeTenantIdRef = useRef<string | null>(null);
  const [recoveredSubscription, setRecoveredSubscription] = useState<RecoveredSubscription | null>(null);

  // Subscription recovery and route enforcement intentionally share one effect
  // so no restricted content is rendered between the two decisions.
  // eslint-disable-next-line complexity
  useEffect(() => {
    if (status === 'loading') return;

    // Public pages check
    const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/new-password', '/verify-email', '/auth/', '/invitation/'];
    if (publicPaths.some(path => pathname?.startsWith(path))) {
      return;
    }

    // Admin pages check
    if (pathname?.startsWith('/admin')) {
      return;
    }

    if (session?.user) {
      // Settings and support must remain reachable when subscription is expired.
      const isRecoveryPage = pathname?.startsWith('/settings')
        || pathname?.startsWith('/subscription')
        || pathname === '/profile'
        || pathname?.startsWith('/console/profile')
        || pathname?.startsWith('/console/organization')
        || pathname?.startsWith('/console/plan')
        || pathname?.startsWith('/hubs/settings');
      const isSupportPage = pathname?.startsWith('/support')
        || pathname?.startsWith('/console/support')
        || pathname?.startsWith('/hubs/support');
      const canAccessWithExpiredSubscription = isRecoveryPage || isSupportPage;
      const tenantId = session.user.tenantId ?? session.tenantId;
      const subscription = session.user.subscription
        ?? session.subscription
        ?? (recoveredSubscription && recoveredSubscription.tenantId === tenantId
          ? recoveredSubscription.subscription
          : undefined);

      // A user without a tenant is handled by the onboarding surface. Trying to
      // resolve a subscription before the tenant exists poisons the one-shot
      // recovery attempt and used to leave a stale error after onboarding.
      if (!tenantId) {
        activeTenantIdRef.current = null;
        attemptedTenantIdRef.current = null;
        return;
      }
      activeTenantIdRef.current = tenantId;
      
      // Attempt recovery once for each selected tenant. A tenant change must
      // always get a fresh lookup, even when a pre-onboarding lookup failed.
      if (!subscription && attemptedTenantIdRef.current !== tenantId) {
        const fetchSubscription = async () => {
          setIsVerifying(true);
          attemptedTenantIdRef.current = tenantId;
          try {
             const service = SubscriptionService.getInstance();
             const subData = await service.getMySubscription();
             
             if (subData && activeTenantIdRef.current === tenantId) {
                const recovered: SubscriptionSummaryDto = {
                  subscriptionId: subData.subscriptionId,
                  status: subData.status?.toString() || '',
                  isActive: subData.isActive,
                  isExpired: subData.isExpired,
                  isExpiringSoon: subData.isExpiringSoon,
                  daysRemaining: subData.daysRemaining,
                  endDate: subData.endDate || '',
                  planName: subData.planName,
                  planDisplayName: subData.planDisplayName,
                  planType: subData.planType?.toString() || '',
                };
                // The API response is authoritative for the guard. Persisting it
                // to the Auth0-backed snapshot is best-effort and must not turn a
                // successful lookup into a false missing-subscription warning.
                setRecoveredSubscription({ tenantId, subscription: recovered });
                setIsVerifying(false);
                void update({ user: { subscription: recovered } }).catch(() => undefined);
                return;
             }
          } catch (error) {
             console.error('Failed to fetch subscription info', error);
          }
          if (activeTenantIdRef.current === tenantId) setIsVerifying(false);
        };
        
        void fetchSubscription();
        return;
      }
      
      // If verifying, do nothing (wait)
      if (isVerifying) return;

      // If subscription is missing (and fetch failed), expired, or inactive without remaining time
      if (!subscription || subscription.isExpired || (!subscription.isActive && subscription.daysRemaining <= 0)) {
        const message = !subscription 
          ? 'Abonelik bilgileri bulunamadı. Lütfen abonelik durumunuzu kontrol edin.'
          : 'Aboneliğinizin süresi dolmuş veya pasif durumdadır. Devam etmek için lütfen aboneliğinizi güncelleyin.';
        
        const shortMessage = !subscription
          ? 'Abonelik bilgileri eksik.'
          : 'Aboneliğinizin süresi dolmuş veya pasif durumdadır. Lütfen aboneliğinizi güncelleyin.';

        if (!canAccessWithExpiredSubscription) {
            if (!toastShownRef.current) {
                toast.error(message, {
                    duration: Infinity, // Kalıcı olsun
                    id: 'subscription-error',
                    action: {
                        label: 'Ayarlara Git',
                        onClick: () => router.push('/console/plan')
                    }
                });
                toastShownRef.current = true;
            }
            
            // Redirect to settings
            router.push('/console/plan');
        } else {
             // On allowed recovery pages, still show error but don't redirect.
             if (!toastShownRef.current) {
                toast.error(shortMessage, {
                     duration: 5000,
                     id: 'subscription-error-settings'
                });
                toastShownRef.current = true;
             }
        }
      } 
      // Warning for expiring soon
      else if (subscription?.isExpiringSoon) {
          if (!toastShownRef.current) {
            toast.warning('Aboneliğiniz yakında sona erecek. Kesinti yaşamamak için lütfen yenileyin.', {
                duration: 6000,
                id: 'subscription-warning',
                action: {
                    label: 'İncele',
                    onClick: () => router.push('/console/plan')
                }
            });
            toastShownRef.current = true;
          }
      }
    }
  }, [session, status, pathname, router, update, isVerifying, recoveredSubscription]);

  // Reset toast flag on session change potentially? 
  // Probably not needed if we want to show it once per route change to invalid state.
  // Actually, if we redirect, component might NOT remount if it's in root layout.
  // But useEffect depends on pathname.
  
  // If we want to show toast again when navigating to another restricted page, we should reset toastShown if valid path.
  useEffect(() => {
     const tenantId = session?.user?.tenantId ?? session?.tenantId;
     const subscription = session?.user?.subscription
       ?? session?.subscription
       ?? (recoveredSubscription && recoveredSubscription.tenantId === tenantId
         ? recoveredSubscription.subscription
         : undefined);
     if (subscription?.isActive && !subscription.isExpired) {
         toastShownRef.current = false;
         toast.dismiss('subscription-error');
         toast.dismiss('subscription-error-settings');
     }
  }, [session, pathname, recoveredSubscription]);

  return <>{children}</>;
}
